import Avatar from '@/components/Avatar';
import { useAuthContext } from '@/context/Auth';
import { DocReviewContext } from '@/context/DocReview';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useSignOut } from '@/hooks/use-sign-out';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, BoxProps, Group, Menu, Badge, Text } from '@mantine/core';
import { useRouter } from 'next/navigation';
import React from 'react';
import Button from '../Button';
import FAIcon from '../FAIcon';

/**
 * Root
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<LoginButtonProps>>((props) => css``);

/**
 * Component(s)
 */

/**
 * LoginButton Component
 */
export interface Props {
  [prop: string]: any;
}

export type LoginButtonProps = Props & BoxProps;

const LoginButton: React.FC<LoginButtonProps> = (props) => {
  const navigate = useRouter();
  const { state: { isVerified, isTrustedDevice  }, dispatch,  } = useAuthContext();
  const docReviewContext = React.useContext(DocReviewContext);
  const [currentUser, authStatus] = useCurrentUser();
  const signOut = useSignOut();

  if (authStatus === 'pending') return null;

  const handleSignOut = async () => {
    // Clear trust and verification status on logout
    if (currentUser) {
      localStorage.removeItem(`trusted_device_${currentUser.uid}`);
      localStorage.removeItem(`2fa_verified_${currentUser.uid}`);
      localStorage.removeItem(`first_login_complete_${currentUser.uid}`);
    }
    await signOut();
  }

  const shouldShowAvatar = currentUser && (isVerified || isTrustedDevice);

  return (
    <Root {...props}>
      {!shouldShowAvatar ? (
        <Group>
          <Button href="/contact" variant="filled" color="primary">
            Contact Sales
          </Button>

          <Button
            component="button"
            transition={false}
            variant="outline"
            color="inherit"
            onClick={() => dispatch({ type: 'OPEN_MODAL' })}
          >
            Login
          </Button>
        </Group>
      ) : (
        <Group>
          <Button
            component="button"
            transition={false}
            variant="outline"
            color="inherit"
            onClick={() => {
              docReviewContext.dispatch({ type: 'OPEN_MODAL' });
            }}
          >
            Request Doc Review
          </Button>

          <Menu width={200} radius="md" position="bottom-end" withinPortal={false}>
            <Menu.Target>
              <Box style={{ position: 'relative' }}>
                <Avatar
                  src={currentUser.photoURL}
                  radius="xl"
                  variant="gradient"
                  size={48}
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ paddingTop: 2 }}>
                    {(currentUser.displayName ?? currentUser.email)?.[0].toUpperCase()}
                  </div>
                </Avatar>
                {isTrustedDevice && (
                  <Badge
                    size="xs"
                    variant="filled"
                    color="var(--color-primary)"
                    style={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      padding: '2px',
                      minWidth: '12px',
                      height: '12px',
                      pointerEvents: 'none'
                    }}
                  />
                )}
              </Box>
            </Menu.Target>

            <Menu.Dropdown
              style={{
                border: 'none',
                borderRadius: 10,
                boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Menu.Label>
                {currentUser.displayName ?? currentUser.email}
                {isTrustedDevice && (
                  <Text size="xs" c="dimmed" mt={4}>
                    Trusted Device
                  </Text>
                )}
              </Menu.Label>

              <Menu.Divider />

              <Menu.Item
                disabled
                rightSection={<FAIcon name="home" size={13} />}
              >
                Dashboard
              </Menu.Item>

              <Menu.Item
                onClick={() => navigate.push('/profile')}
                rightSection={<FAIcon name="user" size={13} />}
              >
                Profile
              </Menu.Item>

              <Menu.Divider />

              <Menu.Item
                rightSection={<FAIcon name="arrow-right-long" size={13} />}
                onClick={handleSignOut}
                color="red"
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      )}
    </Root>
  );
};

export default LoginButton;
